import { RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsPhone } from './use-viewport';

/**
 * Swap and clear, drawn ON TOP of a placed image or clip.
 *
 * ## One component, because the two blocks have to match
 *
 * Image and Sound each carried their own copy of this pair, and they had already drifted on size
 * (32px against 36px) while agreeing on everything else. The same two controls doing the same two
 * things 8px from each other in two editors is one control with one appearance.
 *
 * ## Why a circle and not the usual armed ring
 *
 * `ARMED_DELETE_PAINT` is a hairline warning outline over a half-opacity warning ground, which is
 * right everywhere it is used — on the app's own surfaces, where a hairline reads. These sit on top
 * of a PHOTOGRAPH, where it would not: the same reason the play control needed its own scrim. So
 * the armed state is the circle itself turning warning-coloured, which is legible against anything
 * underneath it, and the outline string is deliberately not used here rather than used badly.
 *
 * That is the distinction `armed-delete.ts`'s own note already draws — one appearance for the ring,
 * with the parts that legitimately differ (its bleed, its rounding) left to the call site. This is
 * the same kind of exception, one step further out.
 *
 * ## Why these arm at all
 *
 * They did not, and the comment saying so argued that re-picking is one click through the Library,
 * so arming would charge a confirmation for a mistake that costs nothing. True about the cost and
 * wrong about the annoyance: these sit on top of the content, where a mis-aimed click at the
 * picture lands on them, and clearing an image you were about to describe is not free in attention
 * even when it is free in data.
 */
export function MediaOverlayControls({
  onSwap,
  swapLabel,
  onClear,
  clearLabel,
}: {
  onSwap: () => void;
  swapLabel: string;
  onClear: () => void;
  clearLabel: string;
}) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  // Matches the pause control 8px away, for the same reason: a coarse pointer on a control that
  // sits over content has no forgiving neighbours to mis-hit into.
  const isPhone = useIsPhone();

  const shape = `flex items-center justify-center rounded-full text-white transition-colors ${isPhone ? 'h-11 w-11' : 'h-9 w-9'}`;
  const idle = 'bg-black/60 hover:bg-black/80';

  return (
    <div className='absolute top-2 right-2 flex gap-1.5'>
      <div aria-live='assertive' aria-atomic='true' className='sr-only'>
        {armed ? t('blocks.actions.deleteArmed') : ''}
      </div>
      <button
        type='button'
        // Swapping cancels a pending clear: two destructive-ish controls side by side should not
        // leave one of them armed behind the other's dialog.
        onClick={() => { setArmed(false); onSwap(); }}
        aria-label={swapLabel}
        title={swapLabel}
        className={`${shape} ${idle}`}
      >
        <RefreshCw className='h-4 w-4' />
      </button>
      {/* A TRASH icon, not an X. The editor's own Close is an X a few pixels above this one, and
          two X's stacked top-right read as the same control — the teacher reaching to dismiss the
          panel kept clearing the asset instead. */}
      <button
        type='button'
        aria-label={armed ? t('blocks.actions.confirmDelete') : clearLabel}
        title={armed ? t('blocks.actions.confirmDelete') : clearLabel}
        onClick={() => { if (armed) { setArmed(false); onClear(); } else setArmed(true); }}
        // Leaving with a mouse disarms; a touch pointer does not, since a tap that lands and lifts
        // would otherwise disarm the thing it just armed.
        onPointerLeave={(e) => { if (armed && e.pointerType !== 'touch') setArmed(false); }}
        onBlur={() => setArmed(false)}
        className={`${shape} ${armed ? 'bg-ws-warning-fg' : idle}`}
      >
        <Trash2 className='h-4 w-4' />
      </button>
    </div>
  );
}
